import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * ExpiryGammaBurst — 0DTE Zero-to-Hero
 *
 * How it works:
 * - Specific to Expiry Day only.
 * - Activates late in the day (e.g., after 13:00).
 * - Scans exactly 1 strike OTM for massive new volume and OI buildup.
 * - This signals a late-day gamma explosion where institutions are loading up cheap OTMs for a fast move.
 */
export const evaluateExpiryGammaBurst: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, gap } = context;
    
    // Check if today is expiry date
    // expiry format is usually 'DD-MMM-YYYY' or similar
    const todayStr = new Date(currentUnixTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-').toUpperCase();
    const expiryStr = expiry.toUpperCase();
    
    // Simplistic check, adapt date format comparison if needed. Assuming formats match for this logic.
    // E.g., '18-DEC-2025'
    if (todayStr !== expiryStr && !expiryStr.includes(todayStr.split('-')[0]!)) {
        // Just a loose fallback check if formats slightly differ. If it's definitely not today, skip.
        // For strictness, let's assume it only runs if the day number matches for now to prevent bugs.
        const todayDay = new Date(currentUnixTime).getDate().toString().padStart(2, '0');
        if (!expiry.startsWith(todayDay)) {
            return null;
        }
    }

    // Check time > 13:00 (Unix time to IST hour)
    const dateObj = new Date(currentUnixTime);
    const hours = dateObj.getUTCHours() + 5;
    const mins = dateObj.getUTCMinutes() + 30;
    const currentISTHour = hours + Math.floor(mins / 60);
    
    // If it's before 1 PM IST, gamma burst is less likely
    if (currentISTHour < 13) return null;

    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    
    // 1 strike OTM
    const ceOtmStrike = atmStrike + gap;
    const peOtmStrike = atmStrike - gap;

    let bestScore = 0;
    let bestSignal: SignalResult | null = null;

    for (const type of ['CE', 'PE'] as const) {
        const strike = type === 'CE' ? ceOtmStrike : peOtmStrike;
        const opt = liveFlatOpts.find((o: any) => o.strike === strike && o.type === type);
        
        if (opt) {
            const vol = opt.volume || 0;
            const oi = opt.oi || 1;
            const oiChg = opt.oiChg || 0;
            
            // Look for explosive volume (e.g., > 1M or 500k depending on index) and high OI addition
            const volThreshold = gap >= 100 ? 200000 : 1000000;
            
            if (vol > volThreshold && oiChg > 0) {
                const volOiRatio = vol / oi;
                
                // If Volume is rotating the OI very fast (high turnover), it's a gamma burst candidate
                if (volOiRatio > 5) {
                    const score = volOiRatio * (oiChg / 1000);
                    
                    if (score > bestScore) {
                        bestScore = score;
                        bestSignal = {
                            trade_type: type === 'CE' ? 'BULL' : 'BEAR',
                            strike: strike,
                            option_type: type,
                            entry_price: opt.ltp || opt.price || opt.close || 0,
                            remarks: `GAMMA BURST 0DTE: ${type} ${strike} (Vol/OI: ${volOiRatio.toFixed(1)}x)`
                        };
                    }
                }
            }
        }
    }

    if (!bestSignal || bestSignal.entry_price <= 0) return null;
    return bestSignal;
};
